import moment, { Moment } from "moment";
import { Fragment, useEffect, useState } from "react";

import Table from "@mui/material/Table";
import TableRow from "@mui/material/TableRow";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import RemoveIcon from "@mui/icons-material/Remove";
import { History } from "@mui/icons-material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { visuallyHidden } from "@mui/utils";
import {
  Badge,
  Box,
  Button,
  Chip,
  Container,
  Grid,
  Paper,
  Stack,
  TableContainer,
  TablePagination,
  TableSortLabel,
  TextField,
  Tooltip,
} from "@mui/material";

import CSV from "../utils/CSV";
import conf from "../../conf";
import Refresh from "../utils/Refresh";
import InfoChip from "../utils/InfoChip";
import { useAuth } from "../auth/AuthProvider";
import EmptyTableRow from "../utils/Empty";
import SectionHeader from "../utils/SectionHeader";
import usePagination from "../../hooks/pagination";
import ProtectedRoute from "../auth/ProtectedRoute";
import { SelectProject } from "../keys/Utils";
import LinearIndeterminate from "../utils/LinearLoader";
import { Event, useEvents } from "../../hooks/events";
import { SelectEmail, SelectEvent, SelectStatus } from "./Utils";
import {
  useConfgOrganizationDownloadTerms,
  useConfgTerms,
} from "../../hooks/confg/base";

const EventRow = (props: { event: Event }) => {
  const { email, updatedAt, status, type, project, key, message, tos } =
    props.event;

  const custom = useConfgOrganizationDownloadTerms();
  const platform = useConfgTerms();

  const enable = tos?.hash;

  const download = () =>
    type.startsWith("ORG_")
      ? custom.mutate({ hash: tos.hash })
      : platform.mutate({ hash: tos.hash });

  const Info = !!message ? <InfoChip info={message} placement="left" /> : "--";

  const Download = (
    <Button
      size="small"
      color="inherit"
      variant="outlined"
      onClick={download}
      disabled={!enable}
    >
      Download
    </Button>
  );

  return (
    <TableRow sx={conf.STYLE.LAST_CHILD_NO_BORDER}>
      <TableCell component="th" scope="row">
        <b>{email}</b>
      </TableCell>

      <TableCell align="right">
        {new Date(updatedAt).toLocaleString() ?? "--"}
      </TableCell>

      <TableCell align="right">
        <Chip
          size="small"
          variant="filled"
          color={status === "failed" ? "error" : "success"}
          label={status}
        />
      </TableCell>

      <TableCell align="right">
        <Chip
          size="small"
          color="primary"
          label={type.replaceAll("ORG_", "").replaceAll("_", " ")}
        />
      </TableCell>

      <TableCell align="right">{key?.name || "--"}</TableCell>

      <TableCell align="right">{project?.name || "--"}</TableCell>

      <TableCell align="right" sx={{ paddingY: 0 }}>
        {Info}
      </TableCell>

      <TableCell align="right">{Download}</TableCell>
    </TableRow>
  );
};

const Events = () => {
  const auth = useAuth();
  const paginate = usePagination({ defaultSort: "updatedAt" });
  const { order, sort, page, limit, body } = paginate;
  const { handleChangeLimit, handleChangePage, handleSort, setBody } = paginate;

  const [type, setType] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [dateLt, setDateLt] = useState<Moment | null>(null);
  const [dateGte, setDateGte] = useState<Moment | null>(null);
  const [project, setProject] = useState<string>("");
  const [openFilters, setOpenFilters] = useState<boolean>(false);

  const events = useEvents({ ...body, type, email, status, project });

  const toggle = () => setOpenFilters(!openFilters);

  const reset = () => {
    setType("");
    setEmail("");
    setStatus("");
    setDateLt(null);
    setDateGte(null);
    setProject("");
  };

  useEffect(() => {
    // if date range is set, apply to post body filter
    if (!!dateGte && !!dateLt) {
      setBody({
        ...body,
        //@ts-ignore
        updatedAt: {
          gte: dateGte.toISOString(),
          lt: dateLt.toISOString(),
        },
      });
    }
  }, [dateGte, dateLt]);

  const Reset = (
    <Tooltip title="Reset all filters">
      <Button onClick={reset}>Reset</Button>
    </Tooltip>
  );

  const Remove = (
    <Tooltip title="remove">
      <RemoveIcon />
    </Tooltip>
  );

  useEffect(() => {
    document.title = "[semiauth] | audit";
  }, []);

  const Filters = () => (
    <Paper elevation={3} sx={{ p: 2 }}>
      <Grid container spacing={2} xs={12} alignItems="center">
        <Grid item sm={2}>
          <SelectEmail
            key={email}
            size="small"
            value={email}
            onChange={setEmail}
          />
        </Grid>
        <Grid item sm={2}>
          <SelectStatus
            key={status}
            size="small"
            value={status}
            options={["success", "failed"]}
            onChange={setStatus}
          />
        </Grid>
        <Grid item sm={2}>
          <SelectEvent
            key={type}
            role={auth.user.activeOrganization.role}
            size="small"
            value={type}
            onChange={setType}
          />
        </Grid>

        {["org.auditor", "org.key.custodian", "org.project.manager"].includes(
          auth.user.activeOrganization.role
        ) && (
          <Grid item sm={2}>
            <SelectProject
              key={project}
              size="small"
              value={project}
              onChange={setProject}
              returnValue="name"
            />
          </Grid>
        )}
        <Grid item sm={1.75}>
          <TextField
            fullWidth
            key={"event-date-gte"}
            id="event-date-gte"
            size="small"
            label="From"
            type="datetime-local"
            value={dateGte?.format("YYYY-MM-DDTHH:mm")}
            onChange={(e: any) => setDateGte(moment(e.target.value))}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item sm={1.75}>
          <TextField
            fullWidth
            key={"event-date-lt"}
            id="event-date-lt"
            size="small"
            label="To"
            type="datetime-local"
            value={dateLt?.format("YYYY-MM-DDTHH:mm")}
            onChange={(e: any) => setDateLt(moment(e.target.value))}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item sm={0.5}>
          {Reset}
        </Grid>
      </Grid>

      <Stack direction="row" my={2} spacing={1} flexWrap="wrap">
        {!!email || !!type || !!status || !!project ? (
          <Fragment>
            {!!email && (
              <Chip
                size="small"
                color="primary"
                label={email}
                onDelete={() => setEmail("")}
                deleteIcon={Remove}
              />
            )}

            {!!status && (
              <Chip
                size="small"
                color="primary"
                label={status}
                onDelete={() => setStatus("")}
                deleteIcon={Remove}
              />
            )}

            {!!type && (
              <Chip
                size="small"
                color="primary"
                label={type.replaceAll("_", " ")}
                onDelete={() => setType("")}
                deleteIcon={Remove}
              />
            )}

            {!!project && (
              <Chip
                size="small"
                label={project}
                color="primary"
                onDelete={() => setProject("")}
                deleteIcon={Remove}
              />
            )}
          </Fragment>
        ) : (
          <Chip size="small" variant="outlined" label="No filters added" />
        )}
      </Stack>
    </Paper>
  );

  return (
    <ProtectedRoute>
      <Container maxWidth="xl">
        <SectionHeader
          title="Audit"
          icon={<History />}
          buttons={[
            <Badge
              key={"events-filter-badge"}
              color="primary"
              badgeContent={
                [type, email, status, project].filter((key) => !!key).length
              }
            >
              <Button
                color="secondary"
                variant="contained"
                onClick={toggle}
                endIcon={<FilterAltIcon />}
                startIcon={
                  <ExpandMoreIcon
                    sx={{
                      transform: openFilters
                        ? "rotate(0deg)"
                        : "rotate(-90deg)",
                      transition: "transform 0.3s",
                    }}
                  />
                }
              >
                Filters
              </Button>
            </Badge>,
            <CSV
              key={"event-csv-export"}
              data={events.data?.data}
              headers={conf.COLUMNS.EVENTS_CSV}
              fileName="semiauth-events"
            />,
            <TablePagination
              key={"event-pagination"}
              component="div"
              page={page}
              count={events.data?.meta?.total || 0}
              rowsPerPage={limit}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeLimit}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />,
            <Refresh
              key={"event-refresh"}
              onClick={events.refetch}
              isFetching={events.isFetching}
            />,
          ]}
        />

        <Paper elevation={3} sx={{ mb: 4 }}>
          <TableContainer sx={{ maxHeight: 630 }}>
            <Table aria-label="collapsible table" stickyHeader>
              <TableHead>
                <TableRow>
                  {conf.COLUMNS.EVENTS.map((column) => (
                    <TableCell
                      key={column.key}
                      align={column.isHeader ? "left" : "right"}
                      sortDirection={order}
                    >
                      <TableSortLabel
                        active={sort === column.key}
                        direction={sort === column.key ? order : "asc"}
                        onClick={(event: React.MouseEvent<unknown>) =>
                          handleSort(event, column.key)
                        }
                      >
                        <b>{column.label}</b>
                        {sort === column.key ? (
                          <Box component="span" sx={visuallyHidden}>
                            {order === "desc"
                              ? "sorted descending"
                              : "sorted ascending"}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    </TableCell>
                  ))}

                  <TableCell align={"right"}>
                    <b>Detail</b>
                  </TableCell>

                  <TableCell align={"right"}>
                    <b>TOS</b>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableHead>
                {openFilters && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Filters />
                    </TableCell>
                  </TableRow>
                )}
              </TableHead>

              <TableBody>
                {events.isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <LinearIndeterminate />
                    </TableCell>
                  </TableRow>
                ) : events.data?.data?.length === 0 ? (
                  <EmptyTableRow type="events" colSpan={8} />
                ) : (
                  events.data?.data?.map((event: Event) => (
                    <EventRow key={event.id} event={event} />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Container>
    </ProtectedRoute>
  );
};

export default Events;
